/**
 * #452 静夜思 - 古诗词水墨动画（优化版 v2）
 * 
 * 时间轴（9秒）：
 * - 标题：静夜思（0-2秒）
 * - 第一句：床前明月光（2-3.5秒）
 * - 第二句：疑是地上霜（3.5-5秒）
 * - 第三句：举头望明月（5-6.5秒）
 * - 第四句：低头思故乡（6.5-8秒）
 * - 作者：李白（8-9秒）
 * 
 * 优化内容（v2）：
 * - ✅ 诗句展示后不消失
 * - ✅ 增加月亮（明月光）
 * - ✅ 增加竹影（东方美学）
 * - ✅ 增加星星（夜晚氛围）
 * - ✅ 增加云雾（诗意）
 * - ✅ 增加窗户剪影（床前）
 * 
 * 创建时间：2026-03-08
 * 更新时间：2026-03-08 09:56
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
  Audio,
} from 'remotion';

// 涟漪数据结构
interface RippleData {
  x: number;
  y: number;
  startTime: number;
  maxRadius: number;
  opacity: number;
}

// 生成随机涟漪
const generateRipples = (count: number, duration: number): RippleData[] => {
  const ripples: RippleData[] = [];
  for (let i = 0; i < count; i++) {
    ripples.push({
      x: 0.2 + random(`ripple-x-${i}`) * 0.6,
      y: 0.2 + random(`ripple-y-${i}`) * 0.6,
      startTime: random(`ripple-start-${i}`) * duration * 0.6,
      maxRadius: 100 + random(`ripple-radius-${i}`) * 150,
      opacity: 0.3 + random(`ripple-opacity-${i}`) * 0.4,
    });
  }
  return ripples;
};

// 单个涟漪组件
const InkRipple: React.FC<{
  ripple: RippleData;
  frame: number;
  fps: number;
  width: number;
  height: number;
}> = ({ ripple, frame, fps, width, height }) => {
  const relativeFrame = frame - ripple.startTime;
  
  if (relativeFrame < 0) return null;

  const progress = interpolate(relativeFrame, [0, 60], [0, 1], {
    extrapolateRight: 'clamp',
  });

  const radius = ripple.maxRadius * progress;
  const opacity = ripple.opacity * (1 - progress * 0.8);
  const strokeWidth = interpolate(progress, [0, 1], [3, 0.5]);

  const centerX = ripple.x * width;
  const centerY = ripple.y * height;

  return (
    <circle
      cx={centerX}
      cy={centerY}
      r={radius}
      fill="none"
      stroke={`rgba(20, 20, 30, ${opacity})`}
      strokeWidth={strokeWidth}
      style={{
        filter: 'url(#ink-blur)',
      }}
    />
  );
};

// 水墨粒子
const InkParticle: React.FC<{
  index: number;
  frame: number;
  fps: number;
  width: number;
  height: number;
}> = ({ index, frame, fps, width, height }) => {
  const seed = random(`particle-${index}`);
  const startX = random(`particle-start-x-${index}`) * width;
  const startY = height + 50;
  
  const speed = 1 + seed * 2;
  const progress = (frame * speed) % 120;
  
  const x = startX + Math.sin(frame * 0.02 + seed * 10) * 50;
  const y = startY - progress * 3;
  const size = 2 + seed * 4;
  const opacity = interpolate(progress, [0, 60, 120], [0, 0.6, 0]);

  if (y < -50) return null;

  return (
    <circle
      cx={x}
      cy={y}
      r={size}
      fill={`rgba(20, 20, 30, ${opacity})`}
      style={{
        filter: 'url(#ink-blur)',
      }}
    />
  );
};

// 诗句组件（优化：不消失）
const PoetryLine: React.FC<{
  text: string;
  startTime: number;
  frame: number;
  fps: number;
  fontSize?: number;
  isTitle?: boolean;
  isAuthor?: boolean;
}> = ({ text, startTime, frame, fps, fontSize = 80, isTitle = false, isAuthor = false }) => {
  const relativeFrame = frame - startTime;
  
  if (relativeFrame < 0) return null;

  const progress = spring({
    frame: relativeFrame,
    fps,
    config: {
      damping: 80,
      stiffness: 150,
      mass: 0.8,
    },
  });

  // 移除 fadeOut，让诗句保持显示
  const opacity = progress;
  const translateY = interpolate(progress, [0, 1], [30, 0]);
  const scale = interpolate(progress, [0, 0.5, 1], [0.8, 1.05, 1]);
  const blur = interpolate(progress, [0, 1], [4, 0]);

  const color = isTitle 
    ? 'rgba(180, 40, 40, 0.9)' 
    : isAuthor 
      ? 'rgba(100, 80, 60, 0.8)' 
      : 'rgba(20, 20, 30, 0.85)';

  return (
    <div
      style={{
        opacity,
        transform: `translateY(${translateY}px) scale(${scale})`,
        filter: `blur(${blur}px)`,
        fontFamily: 'STKaiti, KaiTi, serif',
        fontSize: `${fontSize}px`,
        color,
        textShadow: '0 4px 20px rgba(20, 20, 30, 0.3)',
        marginBottom: isTitle ? '60px' : isAuthor ? '0' : '30px',
        letterSpacing: '8px',
      }}
    >
      {text}
    </div>
  );
};

// 月亮组件
const Moon: React.FC<{
  frame: number;
  fps: number;
  width: number;
  height: number;
}> = ({ frame, fps, width, height }) => {
  const progress = spring({
    frame,
    fps,
    config: { damping: 100, stiffness: 50 },
  });

  const glow = 20 + Math.sin(frame * 0.03) * 5;
  
  return (
    <div
      style={{
        position: 'absolute',
        top: '120px',
        right: '200px',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 255, 240, 0.95) 0%, rgba(255, 248, 220, 0.6) 70%, transparent 100%)',
        boxShadow: `0 0 ${glow}px rgba(255, 248, 220, 0.8), 0 0 ${glow * 2}px rgba(255, 248, 220, 0.4)`,
        opacity: progress,
        transform: `scale(${progress})`,
      }}
    />
  );
};

// 竹影组件
const BambooShadow: React.FC<{
  frame: number;
  width: number;
  height: number;
}> = ({ frame, width, height }) => {
  const sway = Math.sin(frame * 0.01) * 10;
  
  return (
    <svg
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0.08,
      }}
    >
      {/* 左侧竹子 */}
      <g transform={`translate(100, ${height * 0.3}) rotate(${sway})`}>
        <line x1="0" y1="0" x2="0" y2={height * 0.5} stroke="rgba(20, 40, 30, 0.6)" strokeWidth="8" />
        <ellipse cx="0" cy={height * 0.2} rx="15" ry="4" fill="rgba(20, 40, 30, 0.5)" />
        <ellipse cx="0" cy={height * 0.4} rx="15" ry="4" fill="rgba(20, 40, 30, 0.5)" />
      </g>
      
      {/* 右侧竹子 */}
      <g transform={`translate(${width - 100}, ${height * 0.2}) rotate(${-sway})`}>
        <line x1="0" y1="0" x2="0" y2={height * 0.6} stroke="rgba(20, 40, 30, 0.6)" strokeWidth="8" />
        <ellipse cx="0" cy={height * 0.15} rx="15" ry="4" fill="rgba(20, 40, 30, 0.5)" />
        <ellipse cx="0" cy={height * 0.35} rx="15" ry="4" fill="rgba(20, 40, 30, 0.5)" />
        <ellipse cx="0" cy={height * 0.55} rx="15" ry="4" fill="rgba(20, 40, 30, 0.5)" />
      </g>
      
      {/* 竹叶 */}
      {Array.from({ length: 8 }).map((_, i) => {
        const x = i < 4 ? 100 : width - 100;
        const y = height * 0.3 + i * 60;
        const leafSway = Math.sin(frame * 0.015 + i) * 15;
        
        return (
          <ellipse
            key={i}
            cx={x + (i < 4 ? 30 : -30)}
            cy={y}
            rx="25"
            ry="6"
            fill="rgba(20, 40, 30, 0.4)"
            transform={`rotate(${leafSway + (i < 4 ? 30 : -30)})`}
          />
        );
      })}
    </svg>
  );
};

// 星星组件
const Star: React.FC<{
  index: number;
  frame: number;
}> = ({ index, frame }) => {
  const x = random(`star-x-${index}`) * 1920;
  const y = random(`star-y-${index}`) * 400;
  const size = 1 + random(`star-size-${index}`) * 2;
  const twinkle = Math.sin(frame * 0.1 + random(`star-phase-${index}`) * 10) * 0.5 + 0.5;
  
  return (
    <circle
      cx={x}
      cy={y}
      r={size}
      fill={`rgba(255, 255, 255, ${twinkle * 0.8})`}
    />
  );
};

// 云雾组件
const CloudMist: React.FC<{
  frame: number;
  width: number;
  height: number;
}> = ({ frame, width, height }) => {
  const drift1 = Math.sin(frame * 0.005) * 50;
  const drift2 = Math.cos(frame * 0.008) * 40;
  
  return (
    <svg
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: 0.12,
      }}
    >
      {/* 云雾 1 */}
      <ellipse
        cx={width * 0.3 + drift1}
        cy={height * 0.15}
        rx="200"
        ry="80"
        fill="rgba(200, 200, 200, 0.6)"
        filter="url(#ink-blur)"
      />
      
      {/* 云雾 2 */}
      <ellipse
        cx={width * 0.7 + drift2}
        cy={height * 0.25}
        rx="250"
        ry="100"
        fill="rgba(200, 200, 200, 0.5)"
        filter="url(#ink-blur)"
      />
      
      {/* 底部云雾 */}
      <ellipse
        cx={width * 0.5}
        cy={height * 0.9}
        rx="400"
        ry="150"
        fill="rgba(200, 200, 200, 0.4)"
        filter="url(#ink-blur)"
      />
    </svg>
  );
};

// 窗户剪影组件
const WindowSilhouette: React.FC<{
  frame: number;
  fps: number;
  width: number;
  height: number;
}> = ({ frame, fps, width, height }) => {
  const progress = spring({
    frame: frame - 30,
    fps,
    config: { damping: 100, stiffness: 80 },
  });

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        opacity: progress * 0.15,
      }}
    >
      {/* 窗框 */}
      <rect
        x={width * 0.15}
        y={height * 0.4}
        width="200"
        height="300"
        fill="none"
        stroke="rgba(20, 20, 30, 0.8)"
        strokeWidth="12"
      />
      
      {/* 窗格横线 */}
      <line
        x1={width * 0.15}
        y1={height * 0.55}
        x2={width * 0.15 + 200}
        y2={height * 0.55}
        stroke="rgba(20, 20, 30, 0.8)"
        strokeWidth="8"
      />
      
      {/* 窗格竖线 */}
      <line
        x1={width * 0.15 + 100}
        y1={height * 0.4}
        x2={width * 0.15 + 100}
        y2={height * 0.7}
        stroke="rgba(20, 20, 30, 0.8)"
        strokeWidth="8"
      />
    </svg>
  );
};

// 诗句配置
interface PoetryLineConfig {
  text: string;
  startFrame: number;
  fontSize?: number;
  isTitle?: boolean;
  isAuthor?: boolean;
}

// 印章配置
interface SealConfig {
  text: string;
  appearFrame: number;
  position?: { bottom: string; right: string };
  size?: number;
  color?: string;
}

// 音频配置
interface AudioConfig {
  src: string;
  volume?: number;
}

// 主组件 Props
export interface JingYeSiProps {
  /** 诗句列表 */
  lines?: PoetryLineConfig[];
  /** 背景色 */
  backgroundColor?: string;
  /** 印章配置 */
  seal?: SealConfig;
  /** 音频配置，传 null 不播放音频 */
  audio?: AudioConfig | null;
  /** 涟漪数量 */
  rippleCount?: number;
  /** 粒子数量 */
  particleCount?: number;
  /** 星星数量 */
  starCount?: number;
  /** 是否显示月亮 */
  showMoon?: boolean;
  /** 是否显示竹影 */
  showBamboo?: boolean;
  /** 是否显示云雾 */
  showCloud?: boolean;
  /** 是否显示窗户剪影 */
  showWindow?: boolean;
}

const DEFAULT_LINES: PoetryLineConfig[] = [
  { text: '静夜思', startFrame: 0, fontSize: 120, isTitle: true },
  { text: '床前明月光', startFrame: 60 },
  { text: '疑是地上霜', startFrame: 105 },
  { text: '举头望明月', startFrame: 150 },
  { text: '低头思故乡', startFrame: 195 },
  { text: '—— 李白', startFrame: 240, fontSize: 60, isAuthor: true },
];

const DEFAULT_SEAL: SealConfig = {
  text: '李',
  appearFrame: 180,
  position: { bottom: '100px', right: '150px' },
  size: 80,
  color: 'rgba(180, 40, 40, 0.8)',
};

const DEFAULT_AUDIO: AudioConfig = {
  src: 'https://minimax-algeng-chat-tts.oss-cn-wulanchabu.aliyuncs.com/audio%2Ftts-20260308084653-wjFvCCinnpguOKJh.mp3?Expires=1773017214&OSSAccessKeyId=LTAI5tGLnRTkBjLuYPjNcKQ8&Signature=38WzZBmFN%2BkLwLAcRLUsZrjwUYU%3D',
  volume: 1.5,
};

// 主组件
export const JingYeSi: React.FC<JingYeSiProps> = ({
  lines = DEFAULT_LINES,
  backgroundColor = '#F5F1E8',
  seal = DEFAULT_SEAL,
  audio = DEFAULT_AUDIO,
  rippleCount = 8,
  particleCount = 30,
  starCount = 50,
  showMoon = true,
  showBamboo = true,
  showCloud = true,
  showWindow = true,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const ripples = generateRipples(rippleCount, fps * 9);

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* SVG 滤镜定义 */}
      <svg
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
        }}
      >
        <defs>
          <filter id="ink-blur">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
          </filter>
          <filter id="ink-texture">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.04"
              numOctaves="3"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="3"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* 星星层 */}
      {starCount > 0 && (
        <svg
          width={width}
          height={height}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {Array.from({ length: starCount }).map((_, i) => (
            <Star key={i} index={i} frame={frame} />
          ))}
        </svg>
      )}

      {/* 云雾层 */}
      {showCloud && <CloudMist frame={frame} width={width} height={height} />}

      {/* 竹影层 */}
      {showBamboo && <BambooShadow frame={frame} width={width} height={height} />}

      {/* 窗户剪影层 */}
      {showWindow && <WindowSilhouette frame={frame} fps={fps} width={width} height={height} />}

      {/* 涟漪层 */}
      {rippleCount > 0 && (
        <svg
          width={width}
          height={height}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {ripples.map((ripple, i) => (
            <InkRipple
              key={i}
              ripple={ripple}
              frame={frame}
              fps={fps}
              width={width}
              height={height}
            />
          ))}
        </svg>
      )}

      {/* 粒子层 */}
      {particleCount > 0 && (
        <svg
          width={width}
          height={height}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {Array.from({ length: particleCount }).map((_, i) => (
            <InkParticle
              key={i}
              index={i}
              frame={frame}
              fps={fps}
              width={width}
              height={height}
            />
          ))}
        </svg>
      )}

      {/* 月亮层 */}
      {showMoon && <Moon frame={frame} fps={fps} width={width} height={height} />}

      {/* 诗词内容 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
        {lines.map((line, i) => (
          <PoetryLine
            key={i}
            text={line.text}
            startTime={line.startFrame}
            frame={frame}
            fps={fps}
            fontSize={line.fontSize}
            isTitle={line.isTitle}
            isAuthor={line.isAuthor}
          />
        ))}
      </div>

      {/* 装饰性印章 */}
      {seal && (
        <div
          style={{
            position: 'absolute',
            bottom: seal.position?.bottom ?? '100px',
            right: seal.position?.right ?? '150px',
            width: `${seal.size ?? 80}px`,
            height: `${seal.size ?? 80}px`,
            border: `3px solid ${seal.color ?? 'rgba(180, 40, 40, 0.8)'}`,
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: 'STKaiti, KaiTi, serif',
            fontSize: `${(seal.size ?? 80) * 0.45}px`,
            color: seal.color ?? 'rgba(180, 40, 40, 0.8)',
            opacity: spring({
              frame: frame - seal.appearFrame,
              fps,
              config: { damping: 100, stiffness: 200 },
            }),
            transform: `rotate(${interpolate(
              spring({
                frame: frame - seal.appearFrame,
                fps,
                config: { damping: 100, stiffness: 200 },
              }),
              [0, 1],
              [-10, 0]
            )}deg)`,
          }}
        >
          {seal.text}
        </div>
      )}

      {/* 水墨晕染背景装饰 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0.15,
        }}
      >
        <ellipse
          cx={width * 0.2}
          cy={height * 0.3}
          rx={150 + Math.sin(frame * 0.02) * 20}
          ry={100 + Math.cos(frame * 0.02) * 15}
          fill="rgba(20, 20, 30, 0.4)"
          filter="url(#ink-blur)"
        />
        <ellipse
          cx={width * 0.8}
          cy={height * 0.7}
          rx={180 + Math.cos(frame * 0.015) * 25}
          ry={120 + Math.sin(frame * 0.015) * 20}
          fill="rgba(20, 20, 30, 0.3)"
          filter="url(#ink-blur)"
        />
      </svg>

      {/* 配音 */}
      {audio && (
        <Audio
          src={audio.src}
          volume={audio.volume ?? 1}
        />
      )}
    </AbsoluteFill>
  );
};
